import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import Toolbar from './Toolbar';
import theme from './theme';
import styles from './RichTextEditor.module.scss';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import classNames from 'classnames';
import { PageBreakNode, DividerNode, ImageNode } from './Nodes';
import { useTranslation } from 'react-i18next';
import { TableResizerPlugin } from './Plugins';
import ImageDragDropPlugin from './Plugins/ImageDragDropPlugin';
import ImageResizePlugin from './Plugins/ImageResizePlugin';
import { RichTextImageProps } from './Buttons';
import { Affix } from 'antd';

export interface RichTextEditorProps extends RichTextImageProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  readonly?: boolean;
  bordered?: false;
}

export function RichTextEditor(props: RichTextEditorProps) {
  const { disabled, readonly, bordered } = props;
  const { t } = useTranslation();
  const [readyForResizers, setReadyForResizers] = useState(false);

  const initialConfig = {
    namespace: 'MyEditor',
    theme,
    editable: !(disabled || readonly),
    onError(error: any) {
      throw error;
    },
    nodes: [
      HeadingNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      HorizontalRuleNode,
      PageBreakNode,
      DividerNode,
      ImageNode,
    ],
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        className={classNames(styles.editorContainer, {
          [styles.readonly]: readonly,
          [styles.editable]: !readonly,
          [styles.bordered]: bordered !== false,
        })}
      >
        {!readonly && (
          <Affix offsetTop={55} className={classNames(styles.affix)}>
            <Toolbar {...props} />
          </Affix>
        )}

        <div className={styles.contentWrapper}>
          <RichTextPlugin
            contentEditable={<ContentEditable className={styles.editorInput} />}
            placeholder={
              <div className={styles.editorPlaceholder}>
                {t('editor.placeholder')}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>

        {!readonly && (
          <>
            <HistoryPlugin />
            <ListPlugin hasStrictIndent={true} />
            <LinkPlugin />
            <MarkdownShortcutPlugin />
            <ClearEditorPlugin />
            <TablePlugin hasCellMerge={false} hasCellBackgroundColor={true} />
            {readyForResizers && false && <TableResizerPlugin />}
            <ImageDragDropPlugin />
            <OnChangePlugin
              onChange={(editorState) => {
                const json = editorState.toJSON();
                props.onChange?.(JSON.stringify(json));
              }}
            />
          </>
        )}
        <ImageResizePlugin ownerId={props.ownerId} />
        <InitialValuePlugin
          value={props.value}
          onInitialized={() => {
            setReadyForResizers(true);
          }}
        />
      </div>
    </LexicalComposer>
  );
}

export default function LexicalErrorBoundary() {
  const { t } = useTranslation();
  return <div style={{ color: 'red' }}>{t('editor.error')}</div>;
}

function InitialValuePlugin({
  value,
  onInitialized,
}: {
  value?: string;
  onInitialized: () => void;
}) {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;

    if (!value) {
      initialized.current = true;
      onInitialized();
      return;
    }

    initialized.current = true;

    editor.update(() => {
      const json = JSON.parse(value);
      const newState = editor.parseEditorState(json);
      editor.setEditorState(newState);
    });

    onInitialized();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, onInitialized]);

  return null;
}
